<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import { computed } from 'vue'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { useTranslate } from '@/Composables/useTranslate'

const props = defineProps<{
    page: {
        slug: string
        title: Record<string, string>
        content: Record<string, string>
        meta_description: Record<string, string>
        layout: 'default' | 'narrow' | 'wide' | 'landing'
        hero_image_url: string | null
    }
}>()

const { locale } = useTranslate()

const title = computed(() => props.page.title[locale.value] ?? props.page.title.de ?? props.page.slug)
const content = computed(() => props.page.content?.[locale.value] ?? props.page.content?.de ?? '')
const meta = computed(() => props.page.meta_description?.[locale.value] ?? props.page.meta_description?.de ?? '')

const containerClass = computed(() => {
    return {
        default: 'mx-auto max-w-3xl',
        narrow: 'mx-auto max-w-xl',
        wide: 'mx-auto max-w-7xl',
        landing: 'mx-auto max-w-5xl',
    }[props.page.layout] ?? 'mx-auto max-w-3xl'
})
</script>

<template>
    <Head>
        <title>{{ title }}</title>
        <meta v-if="meta" name="description" :content="meta" />
    </Head>
    <PublicLayout>
        <section v-if="page.layout === 'landing' && page.hero_image_url" class="relative h-[40vh] min-h-[280px] w-full overflow-hidden">
            <img :src="page.hero_image_url" :alt="title" class="h-full w-full object-cover" />
            <div class="absolute inset-0 bg-gradient-to-t from-black/70 to-black/10"></div>
            <div class="absolute inset-x-0 bottom-0 px-6 pb-8 lg:px-12">
                <h1 class="mx-auto max-w-5xl text-4xl font-extrabold text-white drop-shadow lg:text-5xl">{{ title }}</h1>
            </div>
        </section>

        <article :class="['px-6 py-10 lg:px-12', containerClass]">
            <h1 v-if="page.layout !== 'landing' || !page.hero_image_url" class="mb-6 text-3xl font-bold">{{ title }}</h1>
            <div class="prose max-w-none dark:prose-invert" v-html="content" />
        </article>
    </PublicLayout>
</template>
